import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { amountToPercentage, percentageToAmount } from '@core/util/number';
import { getInstructionDescription } from '@core/util/trust-ledger';
import {
	BusinessMatterClientWithAssociations,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import {
	MatterClientTrustLedgerInstruction,
	TrustLedgerPaymentSplitType,
	TrustLedgerPaymentType,
} from '@models/trustLedger';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { TrustLedgerSelectors } from '@state/global/trust-ledger/trust_ledger';
import {
	TrustLedger,
	TrustLedgerInstruction,
} from '@state/global/trust-ledger/trust_ledger.models';
import { balanceTrustLedgerInstructions } from '@state/global/trust-ledger/trust_ledger.util';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';
import { TrustLedgerService } from 'src/app/shared/services/trust-ledger/trust-ledger.service';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { GlobalProfileNavType } from 'src/app/private/global-profile/global-profile.types';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

const PaymentMethodOptions: RadioGroupItem<TrustLedgerPaymentType>[] = [
	{ label: 'Cheque', value: TrustLedgerPaymentType.CHEQUE },
	{ label: 'Wire', value: TrustLedgerPaymentType.WIRE },
];

const SplitTypes: RadioGroupItem<TrustLedgerPaymentSplitType>[] = [
	{ label: '% Percentage', value: TrustLedgerPaymentSplitType.PERCENTAGE },
	{ label: '$ Amount', value: TrustLedgerPaymentSplitType.AMOUNT },
];

@UntilDestroy()
@Component({
	selector: 'app-ledger-instructions-modal',
	templateUrl: './ledger-instructions-modal.component.html',
	styleUrls: ['./ledger-instructions-modal.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class LedgerInstructionsModalComponent implements OnInit {
	public paymentMethods = PaymentMethodOptions;
	public splitTypes = SplitTypes;
	public selectedSplitType: TrustLedgerPaymentSplitType;
	public selectedPaymentType: TrustLedgerPaymentType;
	public splitType = TrustLedgerPaymentSplitType;
	public ICON = ICON;
	public totalsSum = 0;
	public showTotalWarning = false;
	public instructions: TrustLedgerInstruction[];
	public trustLedger: TrustLedger;
	public saving;
	public error = false;
	public showTotalError = false;

	@Select(SelectedMatter.trustLedgerPaymentType)
	paymentType$: Observable<TrustLedgerPaymentType>;
	@Select(SelectedMatter.trustLedgerPaymentSplitType)
	paymentSplitType$: Observable<TrustLedgerPaymentSplitType>;

	@Select(TrustLedgerSelectors.getTrustLedger)
	trustLedger$: Observable<TrustLedger>;
	@SelectSnapshot(MatterClients.get_matterClientsWithAssociationMapped)
	clients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	constructor(
		public dialogRef: MatDialogRef<LedgerInstructionsModalComponent>,
		private trustLedgerService: TrustLedgerService,
		private drawerService: DrawerService,
	) {}

	ngOnInit(): void {
		this.paymentType$
			.pipe(untilDestroyed(this))
			.subscribe((x) => (this.selectedPaymentType = x));

		this.paymentSplitType$.pipe(untilDestroyed(this)).subscribe((splitType) => {
			this.selectedSplitType = splitType;
		});
		this.trustLedger$.pipe(untilDestroyed(this)).subscribe((x) => {
			this.instructions = cloneDeep(x.instructions);
			this.trustLedger = cloneDeep(x);
			this.calculateTotals();
		});
	}

	async save(): Promise<boolean> {
		this.saving = true;
		this.error = false;
		this.showTotalError = false;
		this.balanceTotals();

		if (
			!this.arePercentagesValid(
				this.instructions,
				this.trustLedger.totals.potentialPaidToClient,
			)
		) {
			this.showTotalError = true;
			this.saving = false;

			return false;
		}

		try {
			const _newInstructions: {
				[id: string]: MatterClientTrustLedgerInstruction;
			} = {};
			this.instructions.forEach((x) => {
				_newInstructions[x.clientId] = x.trustLedgerInstruction;
			});
			await this.trustLedgerService.saveInstructions(
				this.matterId,
				this.selectedPaymentType,
				this.selectedSplitType,
				_newInstructions,
			);
			this.dialogRef.close();

			return true;
		} catch (err) {
			this.error = true;
			LogRocket.error('Unable to save trust ledger instructions', err);
		} finally {
			this.saving = false;
		}

		return false;
	}

	arePercentagesValid(
		trustLedgerInstructions: TrustLedgerInstruction[],
		potentialAmtPaidToClient: number,
	): boolean {
		const percentageInstructions = trustLedgerInstructions?.filter(
			(i) => i.trustLedgerInstruction.type === 'percentage',
		);

		if (!percentageInstructions?.length) return true;

		// Add up all the portioned percentages and amounts from each set of trust instructions
		const totalsFromPortionedFigures = percentageInstructions.reduce(
			(acc, instruction) => {
				acc.totalFromPortionedPercentage += instruction.portionPercentage;
				acc.totalFromPortionedAmount += instruction.portionAmount;

				return acc;
			},
			{
				totalFromPortionedPercentage: 0,
				totalFromPortionedAmount: 0,
			},
		);

		const { totalFromPortionedPercentage, totalFromPortionedAmount } =
			totalsFromPortionedFigures;

		// Percentages are valid when all the apportioned percentages sum to exactly 100 and the amount apportioned to each instruction sums to the expected payout total
		return (
			totalFromPortionedPercentage === 100 &&
			totalFromPortionedAmount === potentialAmtPaidToClient
		);
	}

	balanceTotals(splitTypeChanged?: boolean): void {
		balanceTrustLedgerInstructions(
			this.instructions,
			this.trustLedger.totals.potentialPaidToClient,
			splitTypeChanged,
		);
	}

	cancel(): void {
		this.dialogRef.close();
	}

	calculateTotals(splitTypeChanged?: boolean): void {
		this.balanceTotals(splitTypeChanged);

		this.instructions.forEach((instruction) => {
			instruction.defaultDescription = getInstructionDescription(
				instruction.clientId,
				this.clients,
				instruction.portionPercentage,
				this.nameFormattingSettings,
			);
		});

		this.totalsSum = this.instructions.reduce(
			(prev, curr) => prev + curr.portionAmount,
			0,
		);

		this.showTotalWarning =
			this.totalsSum !== this.trustLedger.totals.potentialPaidToClient;
	}

	goToClientBankingDetails = (clientId: string): void => {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			title: 'Global Profile',
			data: { clientId, tab: GlobalProfileNavType.BANKING },
		});
		this.dialogRef.close(true);
	};

	amountChanged(instruction: TrustLedgerInstruction, amount: number): void {
		const percentages = this.instructions.filter(
			(i) => i.trustLedgerInstruction.type === 'percentage',
		);

		instruction.trustLedgerInstruction.amount = +amount;
		if (
			percentages.length === 2 && // only 2 %s rows
			!(instruction.trustLedgerInstruction.type === 'amount') // this isn't one of them
		) {
			const otherInstruction = percentages.find(
				(x) => x.clientId !== instruction.clientId,
			);

			if (instruction.trustLedgerInstruction?.type === 'percentage') {
				const percentLeft = 100 - instruction.trustLedgerInstruction.amount;

				if (otherInstruction.trustLedgerInstruction.type === 'percentage') {
					otherInstruction.trustLedgerInstruction.amount = percentLeft;
				} else {
					otherInstruction.trustLedgerInstruction.amount = percentageToAmount(
						percentLeft,
						this.trustLedger.totals.difference,
					);
				}
			} else {
				const amountLeft =
					this.trustLedger.totals.difference -
					instruction.trustLedgerInstruction.amount;

				if (otherInstruction.trustLedgerInstruction.type === 'amount') {
					otherInstruction.trustLedgerInstruction.amount = amountLeft;
				} else {
					otherInstruction.trustLedgerInstruction.amount = amountToPercentage(
						amountLeft,
						this.trustLedger.totals.difference,
					);
				}
			}
		}
		this.calculateTotals();
	}

	changeAmountType(value: TrustLedgerPaymentSplitType): void {
		this.instructions.forEach((instruction) => {
			const newInstruction: MatterClientTrustLedgerInstruction = {
				type: value,
				amount: +instruction.trustLedgerInstruction.amount,
			};

			if (
				value === TrustLedgerPaymentSplitType.PERCENTAGE &&
				instruction.trustLedgerInstruction.type ===
					TrustLedgerPaymentSplitType.AMOUNT
			) {
				newInstruction.amount = amountToPercentage(
					instruction.trustLedgerInstruction.amount,
					this.trustLedger.totals.potentialPaidToClient,
				);
			} else if (
				value === TrustLedgerPaymentSplitType.AMOUNT &&
				instruction.trustLedgerInstruction.type ===
					TrustLedgerPaymentSplitType.PERCENTAGE
			) {
				newInstruction.amount = percentageToAmount(
					instruction.trustLedgerInstruction.amount,
					this.trustLedger.totals.potentialPaidToClient,
				);
			}

			instruction.trustLedgerInstruction = newInstruction;
		});
		const splitTypeChanged = true;
		this.calculateTotals(splitTypeChanged);
	}
}
